import React, { Fragment, useEffect, useState } from 'react';
import { Button, Image, Modal, notification } from 'antd';
import InputText from '../../Commons/InputText/InputText';
import { useParams } from 'react-router-dom';
import validator from 'validator';
import { useDispatch, useSelector } from 'react-redux';
import { startLinkedinSession } from '../../../Redux/Features/startLinkedinSessionSlice';
import { linkedinSessionStatus, setLinkedinSessionStateNull } from '../../../Redux/Features/linkedinSessionStatusSlice';
import { linkedinPin } from '../../../Redux/Features/linkedinPinSlice';
import CustomModal from '../../Commons/Modal/Modal';
import { getLinkedinProfiles } from '../../../Redux/Features/getLinkedinProfilesSlice';
import { updateLinkedinProfile } from '../../../Redux/Features/updateLinkedinProfileSlice';
import { getLinkedinProfileById } from '../../../Redux/Features/getLinkedinProfileSlice';
import { getCookie, removeCookie, setCookie } from '../../../utils/helper';
import styles from './UpdateLinkedinProfile.module.scss';
import ScreenLoad from '../../Commons/ScreenLoad/ScreenLoad';
import InputCountry from '../../Commons/InputCountry/InputCountry';
import { BASE_URL } from '../../../Config';
import { resumeFailedCampaigns, resetResumeFailedCampaignsState } from '../../../Redux/Features/resumeFailedCampaignsSlice';

const UpdateLinkedinProfile = ({ open, setOpen }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { profile, isError: profileError, errors: profileErrors } = useSelector((state) => state?.getLinkedinProfile);
  const { isLoading: updateProfileLoading } = useSelector((state) => state?.updateLinkedinProfile);
  const { isSuccess: startSessionSuccess, sessionId } = useSelector((state) => state?.startLinkedinSession);
  const {
    isSuccess: sessionStatusSuccess,
    isLoading: sessionStatusLoading,
    data,
  } = useSelector((state) => state?.linkedinSessionStatus);
  const { isSuccess: resumeSuccess, isError: resumeError, data: resumeData, error: resumeErr } = useSelector(
    (state) => state.resumeFailedCampaigns || {}
  );
  const [clientError, setClientError] = useState({});
  const [statusCheckLoading, setStatusCheckLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [keyToStartSession, setKeyToStartSession] = useState(false);
  const [pin, setPin] = useState('');
  const [sessionMessage, setSessionMessage] = useState('');
  const [modalBtnLoading, setModalBtnLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    country: '',
  });
  useEffect(() => {
    dispatch(getLinkedinProfileById(id));
  }, [id]);

  useEffect(() => {
    setFormData({
      email: profile?.linkedin_email,
      password: profile?.linkedin_password,
      country: profile?.country ? profile?.country : '',
    });
  }, [profile]);

  useEffect(() => {
    setStatusCheckLoading(false);
    setKeyToStartSession(false);
    setSessionMessage('');
    dispatch(setLinkedinSessionStateNull());
    handleRevertUpdation();
  }, []);

  const setFeild = (feildName, feildValue) => {
    setFormData({ ...formData, [feildName]: feildValue });
  };
  const handleConnect = async () => {
    let isErr = false;
    let errors = {};

    if (!validator.isEmail(formData?.email)) {
      isErr = true;
      errors.email = 'Email is invalid';
    }
    if (!validator.isLength(formData?.password, { min: 6 })) {
      isErr = true;
      errors.password = 'Password should be 6 or more characters long';
    }
    if (validator.isEmpty(formData?.email)) {
      isErr = true;
      errors.email = 'Email is required';
    }
    if (validator.isEmpty(formData?.password)) {
      isErr = true;
      errors.password = 'Password is required';
    }
    if (validator.isEmpty(formData?.country)) {
      isErr = true;
      errors.country = 'Country is required';
    }

    if (isErr) {
      isErr = false;
      setClientError(errors);
    } else {
      setClientError({});
      const data = {
        data: {
          linkedin_email: formData?.email,
          linkedin_password: formData?.password,
          country: formData?.country,
        },
        onSuccess: handleProfileSuccess,
        onError: () => {
          setStatusCheckLoading(false);
        },
        id,
      };
      dispatch(updateLinkedinProfile(data));
      setStatusCheckLoading(true);
      setOpen && setOpen(false);
    }
  };

  const handleProfileSuccess = () => {
    let data = {
      linkedinProfile: {
        id,
      },
    };
    setCookie('previousProfileData', {
      linkedin_email: profile?.linkedin_email,
      linkedin_password: profile?.linkedin_password,
      id,
    });
    dispatch(startLinkedinSession(data));
    notification.success({ message: 'Status', description: 'Connecting Linkedin...' });
    setKeyToStartSession(true);
    dispatch(setLinkedinSessionStateNull());
  };

  useEffect(() => {
    if (!sessionId) return;
    let timeInterval;
    if (
      startSessionSuccess &&
      !sessionStatusLoading &&
      keyToStartSession &&
      data?.status !== 'SUCCESS' &&
      data?.status !== 'FAILED'
    ) {
      timeInterval = setTimeout(() => {
        let data = {
          sessionId,
        };
        dispatch(linkedinSessionStatus(data));
      }, 3000);
    }
    return () => {
      clearTimeout(timeInterval);
    };
  }, [startSessionSuccess, sessionId, sessionStatusSuccess]);

  const handleClosedTheSession = (sessionClosedMessage) => {
    sessionClosedMessage && setSessionMessage(sessionClosedMessage);
    setOpen && setOpen(false);
    setModalBtnLoading(false);
    handleRevertUpdation();
    setStatusCheckLoading(false);
    setIsModalOpen(false);
    setKeyToStartSession(false);
  };

  useEffect(() => {
    if (resumeSuccess && resumeData?.success) {
      notification.success({
        message: 'Campaigns Resumed',
        description: 'All related failed campaigns have been resumed.',
      });
      dispatch(resetResumeFailedCampaignsState());
    } else if (resumeError) {
      notification.error({
        message: 'Error',
        description: resumeErr?.message || resumeData?.message || 'Failed to resume campaigns.',
      });
      dispatch(resetResumeFailedCampaignsState());
    }
  }, [resumeSuccess, resumeError, resumeData, resumeErr, dispatch]);

  const onSuccessClosed = () => {
    setModalBtnLoading(false);
    setIsModalOpen(false);
    setStatusCheckLoading(false);
    removeCookie('previousProfileData');
    dispatch(getLinkedinProfiles());
    notification.success({ message: 'Status', description: 'Successfully logged in' });
    setKeyToStartSession(false);
    setOpen && setOpen(false);

    dispatch(resumeFailedCampaigns(id));
  };

  useEffect(() => {
    if (!data) return;
    if (!keyToStartSession) return;
    if (data?.status === 'IN_PROGRESS') {
      setModalBtnLoading(true);
      setSessionMessage('');
    } else if (data?.status === 'WAITING_FOR_PIN') {
      setOpen && setOpen(false);
      setSessionMessage(data?.message);
      setStatusCheckLoading(false);
      setModalBtnLoading(false);
      setIsModalOpen(true);
    } else if (data?.status === 'SUCCESS') {
      onSuccessClosed();
    } else if (data?.status === 'FAILED') {
      handleClosedTheSession(data?.message);
    } else if (data?.status === 'WAITING_FOR_LINKEDIN_APP') {
      notification.warning({ message: 'Status', description: 'Please allow login using Linkedin app in your phone' });
    }
  }, [data]);

  useEffect(() => {
    if (!sessionMessage) return;
    notification.error({ message: 'Status', description: sessionMessage, duration: 0 });
  }, [sessionMessage]);

  const handleRevertUpdation = () => {
    let previousData = getCookie('previousProfileData');
    if (!previousData?.id) return;
    const data = {
      data: {
        linkedin_email: previousData?.linkedin_email,
        linkedin_password: previousData?.linkedin_password,
      },
      id: previousData?.id,
      onSuccess: () => {
        removeCookie('previousProfileData');
        dispatch(getLinkedinProfileById(id));
      },
    };
    dispatch(updateLinkedinProfile(data));
  };

  const handleConfirmPin = () => {
    let isErr = false;
    let error = {};

    if (!validator.isLength(pin, { min: 6 })) {
      isErr = true;
      error.pin = 'Pin should be 6 characters long';
    }
    if (validator.isEmpty(pin)) {
      isErr = true;
      error.pin = 'Pin is required';
    }
    if (isErr) {
      isErr = false;
      setClientError(error);
    } else {
      setClientError({});
      let data = {
        data: {
          sessionId,
          pin,
        },
        onSuccess: () => {
          setPin('');
          let data = {
            sessionId,
          };
          dispatch(linkedinSessionStatus(data));
        },
      };
      dispatch(linkedinPin(data));
    }
  };

  return (
    <Fragment>
      {statusCheckLoading && <ScreenLoad info={''} />}
      <div className={styles.add_linkedin_profile_container}>
        <Modal
          open={open}
          cancelButtonProps={{ style: { display: 'none' } }}
          okButtonProps={{ style: { display: 'none' } }}
          centered
          closeIcon={false}
          className={'linkedin_modal_container'}
        >
          <div className={styles.linkedin_modal}>
            <div className={styles.header_details_container}>
              <div className={styles.auto_container}>
                <div className={styles.user_details}>
                  <Image
                    src={profile?.linkedin_profile_pic ? profile?.linkedin_profile_pic : '/Assets/placeholder.png'}
                    alt={'user default image'}
                    preview={false}
                    onError={(e) => {
                      e.target.src = '/Assets/placeholder.png';
                    }}
                  />
                  <h2>{profile?.linkedin_name ? profile?.linkedin_name : 'N/A'}</h2>
                </div>
                <div className={styles.close_icon}>
                  <Image
                    src={'/Assets/close_icon.svg'}
                    alt={'Close icon'}
                    preview={false}
                    onClick={() => {
                      setClientError({});
                      setOpen && setOpen(false);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className={styles.form_container}>
              <InputCountry
                size={'medium'}
                label={'Linkedin Account Country'}
                placeholder={'Country'}
                value={formData?.country}
                onChange={(e) => setFeild('country', e?.value)}
                error={clientError?.country ? clientError?.country : false}
              />
              <div className={styles.divider}></div>
              <InputText
                size="medium"
                type={'email'}
                placeholder={'Email'}
                label={'Linkedin Email'}
                value={formData?.email}
                onChange={(e) => setFeild('email', e?.target?.value)}
                error={clientError?.email ? clientError?.email : false}
              />
              <div className={styles.divider}></div>
              <InputText
                size="medium"
                type={'password'}
                placeholder={'Password'}
                label={'Linkedin Password'}
                value={formData?.password}
                onChange={(e) => setFeild('password', e?.target?.value)}
                error={clientError?.password ? clientError?.password : false}
              />
              <div className={styles.button_container}>
                <Button
                  className="ant-btn-middle"
                  type="primary"
                  loading={updateProfileLoading}
                  disabled={updateProfileLoading}
                  onClick={handleConnect}
                >
                  Update LinkedIn
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      </div>
      {isModalOpen && modalBtnLoading ? (
        <ScreenLoad info={'We are trying to log you in'} />
      ) : (
        <CustomModal
          isOpen={isModalOpen}
          title={'Confirm Your Pin'}
          description={'Enter your pin to continue'}
          okText={'Confirm'}
          onSubmit={() => handleConfirmPin()}
          onCancel={() => handleClosedTheSession()}
          btnLoading={modalBtnLoading}
        >
          <InputText
            label={'Pin'}
            size="medium"
            type={'number'}
            placeholder={'Enter your pin'}
            value={pin}
            onChange={(e) => setPin(e?.target?.value)}
            error={clientError?.pin ? clientError?.pin : false}
          />
        </CustomModal>
      )}
    </Fragment>
  );
};

export default UpdateLinkedinProfile;
